import { FunctionComponent, useLayoutEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import { OnlyBot, OnlyBotMetadata, packVoxelSpace, Point3, Point3Set } from '@animavirtuality/onlybots-core';

const textColor = '#FFFFFF';
const outlineColor = '#512CBB';
const comicSans = '"Comic Sans", "Comic Sans MS", "Chalkboard", "ChalkboardSE-Regular", sans-serif';

type Facet = {
    normal: Point3;
    vertices: [Point3, Point3, Point3];
};

type CubeVertices = [Point3, Point3, Point3, Point3, Point3, Point3, Point3, Point3];

function determineFacets(length: Point3, set: Point3Set, voxel: Point3): Facet[] {
    const z = voxel.z * -1;
    const facets: Facet[] = [];
    const vertices: CubeVertices = [
        new Point3(voxel.x, voxel.y, z),
        new Point3(voxel.x + 1, voxel.y, z),
        new Point3(voxel.x + 1, voxel.y + 1, z),
        new Point3(voxel.x, voxel.y + 1, z),
        new Point3(voxel.x, voxel.y, z - 1),
        new Point3(voxel.x + 1, voxel.y, z - 1),
        new Point3(voxel.x + 1, voxel.y + 1, z - 1),
        new Point3(voxel.x, voxel.y + 1, z - 1),
    ];

    // px
    if (voxel.x >= length.x - 1 || !set.has(voxel.x + 1, voxel.y, voxel.z)) {
        facets.push({
            normal: new Point3(1, 0, 0),
            vertices: [vertices[5], vertices[2], vertices[1]],
        });
        facets.push({
            normal: new Point3(1, 0, 0),
            vertices: [vertices[5], vertices[6], vertices[2]],
        });
    }

    // nx
    if (voxel.x <= 0 || !set.has(voxel.x - 1, voxel.y, voxel.z)) {
        facets.push({
            normal: new Point3(-1, 0, 0),
            vertices: [vertices[0], vertices[3], vertices[4]],
        });
        facets.push({
            normal: new Point3(-1, 0, 0),
            vertices: [vertices[4], vertices[3], vertices[7]],
        });
    }

    // py
    if (voxel.y >= length.y - 1 || !set.has(voxel.x, voxel.y + 1, voxel.z)) {
        facets.push({
            normal: new Point3(0, 1, 0),
            vertices: [vertices[2], vertices[6], vertices[3]],
        });
        facets.push({
            normal: new Point3(0, 1, 0),
            vertices: [vertices[3], vertices[6], vertices[7]],
        });
    }

    // ny
    if (voxel.y <= 0 || !set.has(voxel.x, voxel.y - 1, voxel.z)) {
        facets.push({
            normal: new Point3(0, -1, 0),
            vertices: [vertices[0], vertices[4], vertices[1]],
        });
        facets.push({
            normal: new Point3(0, -1, 0),
            vertices: [vertices[1], vertices[4], vertices[5]],
        });
    }

    // nz
    if (voxel.z >= length.z - 1 || !set.has(voxel.x, voxel.y, voxel.z + 1)) {
        facets.push({
            normal: new Point3(0, 0, 1),
            vertices: [vertices[4], vertices[7], vertices[5]],
        });
        facets.push({
            normal: new Point3(0, 0, 1),
            vertices: [vertices[5], vertices[7], vertices[6]],
        });
    }

    // pz
    if (voxel.z <= 0 || !set.has(voxel.x, voxel.y, voxel.z - 1)) {
        facets.push({
            normal: new Point3(0, 0, -1),
            vertices: [vertices[0], vertices[1], vertices[2]],
        });
        facets.push({
            normal: new Point3(0, 0, -1),
            vertices: [vertices[2], vertices[3], vertices[0]],
        });
    }

    return facets;
}

export const STL: FunctionComponent = () => {
    const [tokenId, setTokenId] = useState<string>('');
    const [error, setError] = useState<string>('');

    useLayoutEffect(() => {
        document.body.style.background = '#6A58E7';
        return () => {
            document.body.style.background = '';
        };
    }, []);

    const generate = async (): Promise<void> => {
        setError('');
        console.log(`Generating STL for ID: ${tokenId}`);
        const res = await fetch(`https://r.only.bot/api/tokenURI/${tokenId}`);

        if (!res.ok) {
            setError(`Failed to fetch token uri for ID: ${tokenId}`);
            return;
        }

        const { uri } = await res.json();
        const metadataRes = await fetch('https://ipfs-mirror.anima.supply/' + uri.slice('ipfs://'.length));

        if (!metadataRes.ok) {
            setError(`Failed to fetch metadata for token ID: ${tokenId}`);
            return;
        }

        const metadata = (await metadataRes.json()) as OnlyBotMetadata;

        const lines = [`solid ${metadata.name}`];
        const bot = OnlyBot.fromJSON(metadata.ext.source);

        const voxels = bot.voxels();
        const length = packVoxelSpace(voxels).toLength();
        const set = new Point3Set(voxels);
        const facets: Facet[] = [];

        voxels.forEach((voxel) => {
            facets.push(...determineFacets(length, set, voxel));
        });

        facets.forEach(({ normal, vertices }) => {
            lines.push(`  facet normal ${normal.x.toString(10)} ${normal.y.toString(10)} ${normal.z.toString(10)}`);
            lines.push('    outer loop');
            vertices.forEach((vertex) => {
                lines.push(`      vertex ${vertex.x.toString(10)} ${vertex.y.toString(10)} ${vertex.z.toString(10)}`);
            });
            lines.push('    endloop');
            lines.push('  endfacet');
        });

        lines.push(`endsolid ${metadata.name}`);
        saveAs(new Blob([lines.join('\n') + '\n'], { type: 'application/sla;charset=ascii' }), `${metadata.name}.stl`);
    };

    return (
        <div
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                paddingY: '8vh',
                paddingX: '30px',
                color: textColor,
                '--webkit-font-smoothing': 'antialiased',
                overflowX: 'hidden',
            }}
        >
            <h1
                sx={{
                    marginTop: 0,
                    fontSize: '48px',
                    fontFamily: comicSans,
                    textShadow: `-3px -3px 0 ${outlineColor}, 0 -3px 0 ${outlineColor}, 3px -3px 0 ${outlineColor}, 3px 0 0 ${outlineColor}, 3px 3px 0 ${outlineColor}, 0 3px 0 ${outlineColor}, -3px 3px 0 ${outlineColor}, -3px 0 0 ${outlineColor}`,
                }}
            >
                Generate STL
            </h1>
            <form
                onSubmit={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void generate();
                }}
                sx={{
                    display: 'flex',
                    maxWidth: '400px',
                    gap: '16px',

                    '& button': {
                        background: '#5FC0BA',
                        fontSize: '20px',
                        paddingY: '12px',
                        paddingX: '24px',
                        color: textColor,
                        borderRadius: '12px',
                        fontWeight: 900,
                        textShadow: '1px 1px 0 #000',
                    },

                    '& input': {
                        fontSize: '20px',
                    },
                }}
            >
                <input
                    type="number"
                    min={1}
                    max={2048}
                    placeholder={'Token ID'}
                    value={tokenId}
                    onChange={(e) => setTokenId(e.target.value)}
                    sx={{ width: '200px' }}
                />
                <button type="submit">Go</button>
            </form>
            <div
                sx={{
                    maxWidth: '400px',
                    marginTop: '32px',
                    fontFamily: 'sans-serif',
                    fontStyle: 'italic',
                    color: '#0fffff',
                }}
            >
                {error}
            </div>
        </div>
    );
};
